<template>
  <div class="customer-destination-authorization-page">
    <p class="text-muted mb-2">
      {{ $t('Destination Authorization Desc') }}
    </p>

    <b-alert show class="mb-2 d-flex align-items-center" variant="warning">
      <div class="alert-body d-flex align-items-center">
        <feather-icon icon="InfoIcon" class="mr-1 top-0" size="22" />
        <span>{{ $t('Destination Authorization Alert') }}</span>
      </div>
    </b-alert>

    <b-card no-body class="mb-2">
      <b-card-body>
        <h5 class="mb-2">{{ $t('Add New Restriction') }}</h5>
        <b-row class="align-items-end">
          <b-col md="5" class="mb-1 mb-md-0">
            <b-form-group :label="$t('Provider')" label-for="provider-select" class="mb-0">
              <v-select
                id="provider-select"
                v-model="newRestriction.provider_id"
                :options="providerOptions"
                :reduce="provider => provider.value"
                label="text"
                :placeholder="$t('Select Provider')"
              >
                <span slot="no-options">{{ $t('No records found') }}</span>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col md="5" class="mb-1 mb-md-0">
            <b-form-group :label="$t('Country')" label-for="country-select" class="mb-0">
              <v-select
                id="country-select"
                v-model="newRestriction.country_code"
                :options="countryOptions"
                :reduce="country => country.value"
                label="text"
                :placeholder="$t('Search Country')"
              >
                <span slot="no-options">{{ $t('No records found') }}</span>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-button
              variant="primary"
              class="w-100"
              :disabled="!isFormValid || submitting"
              @click="addRestriction"
            >
              <b-spinner v-if="submitting" small class="mr-50" />
              {{ $t('Close') }}
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-row>
      <b-col md="4">
        <v-select
          class="card"
          v-model="filterProvider"
          :options="providerFilterOptions"
          :reduce="provider => provider.value"
          label="text"
          :placeholder="$t('Filter by provider')"
          @input="loadItems"
        >
          <span slot="no-options">{{ $t('No records found') }}</span>
        </v-select>
      </b-col>
    </b-row>

    <b-card no-body>
      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :rows="rows"
        :columns="columns"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: $t('Rows per page'),
          ofLabel: $t('ofLabel'),
          nextLabel: $t('nextLabel'),
          prevLabel: $t('prevLabel'),
          dropdownAllowAll: false
        }"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'provider'">
            {{ props.row.provider ? props.row.provider.name : '-' }}
          </span>
          <span v-else-if="props.column.field === 'country'">
            {{ props.row.country ? props.row.country.name : '-' }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill variant="light-warning">
              {{ $t('Closed') }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'actions'">
            <b-button variant="flat-info" class="p-0" @click="removeRestriction(props.row.id)">
              <span class="text-info">{{ $t('Remove') }}</span>
            </b-button>
          </span>
        </template>
      </vue-good-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BAlert,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BBadge,
  BSpinner
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import vSelect from 'vue-select';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';

export default {
  components: {
    BCard,
    BCardBody,
    BAlert,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BBadge,
    BSpinner,
    VueGoodTable,
    vSelect
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      submitting: false,
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterProvider: '',
      providers: [],
      countries: [],
      newRestriction: {
        provider_id: null,
        country_code: null
      }
    };
  },
  computed: {
    customerId() {
      return this.$route.params.id;
    },
    isFormValid() {
      return this.newRestriction.provider_id && this.newRestriction.country_code;
    },
    providerOptions() {
      return this.providers.map(p => ({ value: p.id, text: p.name }));
    },
    providerFilterOptions() {
      return this.providerOptions;
    },
    countryOptions() {
      return this.countries.map(c => ({ value: c.value, text: c.text }));
    },
    columns() {
      return [
        { label: this.$t('Provider'), field: 'provider' },
        { label: this.$t('Country'), field: 'country' },
        { label: this.$t('Usage Status'), field: 'status' },
        {
          label: this.$t('Actions'),
          field: 'actions',
          tdClass: 'text-right',
          thClass: 'text-right'
        }
      ];
    }
  },
  async created() {
    await this.fetchDependencies();
    this.loadItems();
  },
  methods: {
    async fetchDependencies() {
      try {
        const [providersRes] = await Promise.all([
          useService.getAllProviders({ limit: 100 }),
          this.$store.dispatch('app/getCountries')
        ]);
        if (providersRes && providersRes.data) {
          this.providers = providersRes.data;
        }
        this.countries = this.$store.getters['app/countries'] || [];
      } catch (err) {
        this.showErrorMessage(err[0]?.message || this.$t('An error occurred'));
      }
    },
    getProviderName(id) {
      const provider = this.providers.find(p => p.id === id);
      return provider ? provider.name : id;
    },
    getCountryName(code) {
      const country = this.countries.find(c => c.value === code);
      return country ? country.text : code;
    },
    async loadItems() {
      this.isLoading = true;
      try {
        const params = {
          ...this.filter,
          customer: this.customerId,
          ...(this.filterProvider ? { provider: this.filterProvider } : {})
        };
        const res = await useService.getCustomerRestrictedDestinations(params);
        this.rows = res.data;
        this.totalRecords = res.meta ? res.meta.total : res.data.length;
      } catch (err) {
        this.showErrorMessage(err.message || this.$t('An error occurred'));
      } finally {
        this.isLoading = false;
      }
    },
    async addRestriction() {
      this.submitting = true;
      try {
        await useService.createCustomerRestrictedDestination({
          customer_id: parseInt(this.customerId),
          provider_id: this.newRestriction.provider_id,
          country_id: this.newRestriction.country_code
        });
        this.showSuccessMessage(this.$t('Successful'));
        this.newRestriction = { provider_id: null, country_code: null };
        this.filter.page = 1;
        this.loadItems();
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          this.showErrorMessage(err.response.data.message);
        } else {
          this.showErrorMessage(err[0].message || this.$t('An error occurred'));
        }
      } finally {
        this.submitting = false;
      }
    },
    removeRestriction(restrictionId) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Sure to delete?')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          try {
            await useService.deleteCustomerRestrictedDestination(restrictionId);
            this.showSuccessMessage(this.$t('Successful'));
            this.loadItems();
          } catch (err) {
            this.showErrorMessage(err.message || this.$t('An error occurred'));
          }
        }
      });
    },
    onPageChange(params) {
      this.filter.page = params.currentPage;
      this.loadItems();
    },
    onPerPageChange(params) {
      this.filter.page = 1;
      this.filter.limit = params.currentPerPage;
      this.loadItems();
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.customer-destination-authorization-page {
  .badge-square {
    border-radius: 4px;
    padding: 0.3rem 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: #7367f0 !important;
    background-color: rgba(115, 103, 240, 0.12) !important;
  }

  .badge.badge-warning.badge-square {
    background-color: #ffc254 !important;
    color: #fff !important;
  }

  .base-alert {
    background-color: #fdf5e8 !important;
    border-color: #fce8c0 !important;
    color: #4b4b4b !important;

    .alert-body {
      color: #4b4b4b !important;
    }
    .feather-icon {
      color: #4b4b4b !important;
    }
  }

  .btn-info {
    background-color: #92d1de !important;
    border-color: #92d1de !important;
    &:hover {
      background-color: darken(#92d1de, 10%) !important;
      border-color: darken(#92d1de, 10%) !important;
    }
  }

  .btn-flat-info {
    color: #92d1de !important;
    &:hover {
      background-color: rgba(146, 209, 222, 0.12) !important;
    }
  }

  .table-icon-buttons .btn {
    padding: 0.3rem 0.6rem !important;
  }
}
</style>
